// The mass units Emissions can display its totals in. Screens should offer every unit in `emissionsMassUnitList`
// rather than assuming any particular one, so that adding a unit here (and its ID in
// cross-app-shared/types/emissions-mass-units.ts) makes it available everywhere.
//
// Totals are accumulated in pounds and converted to the displayed unit only at display time, so that toggling the
// unit never requires recomputing (or separately tracking) every total.

import { Pound, UnitsTracker } from '../formulas/units.ts';
import {
  type EmissionsMassUnitID,
  emissionsMassUnitIDs,
} from '../../../cross-app-shared/types/emissions-mass-units.ts';

export type EmissionsMassUnitSystem = 'us' | 'metric';

interface EmissionsMassUnitDefinition {
  system: EmissionsMassUnitSystem;
  /** For pickers, column headers, and running text (i.e. "Tons"). */
  name: string;
  /** For compact unit labels next to a value (i.e. "tons" or "lbs"). */
  abbreviation: string;
  /** Summary-tier decimal places, so that a column of totals lines up. */
  decimalPlaces: number;
  /** How the unit is written in a formula or a limit (i.e. "ton"). */
  unitsString: string;
}

const definitions: Record<EmissionsMassUnitID, EmissionsMassUnitDefinition> = {
  tons: { system: 'us', name: 'Short Tons', abbreviation: 'tons', decimalPlaces: 3, unitsString: 'ton' },
  hundredweight: { system: 'us', name: 'Hundredweight', abbreviation: 'cwt', decimalPlaces: 3, unitsString: 'cwt' },
  pounds: { system: 'us', name: 'Pounds', abbreviation: 'lbs', decimalPlaces: 2, unitsString: 'lb' },
  ounces: { system: 'us', name: 'Ounces', abbreviation: 'oz', decimalPlaces: 1, unitsString: 'oz' },
  grains: { system: 'us', name: 'Grains', abbreviation: 'grains', decimalPlaces: 0, unitsString: 'grain' },
  tonnes: { system: 'metric', name: 'Metric Tons', abbreviation: 'tonnes', decimalPlaces: 3, unitsString: 'tonne' },
  kilograms: { system: 'metric', name: 'Kilograms', abbreviation: 'kg', decimalPlaces: 2, unitsString: 'kg' },
  grams: { system: 'metric', name: 'Grams', abbreviation: 'g', decimalPlaces: 1, unitsString: 'g' },
  milligrams: { system: 'metric', name: 'Milligrams', abbreviation: 'mg', decimalPlaces: 0, unitsString: 'mg' },
};

const systemNames: Record<EmissionsMassUnitSystem, string> = {
  us: 'US Customary',
  metric: 'Metric',
};

export interface EmissionsMassUnit extends EmissionsMassUnitDefinition {
  id: EmissionsMassUnitID;
  units: UnitsTracker;
  /** How many of this unit make up one pound. */
  perPound: number;
}

const poundsUnits = UnitsTracker.fromSingleUnit(Pound);

export const emissionsMassUnits = Object.fromEntries(
  emissionsMassUnitIDs.map((id) => {
    const definition = definitions[id];
    const units = UnitsTracker.fromParsedUnitString(definition.unitsString);
    const perPound = poundsUnits.convertTo(units, 1);
    if (typeof perPound !== 'number') throw perPound;
    return [id, { ...definition, id, units, perPound }];
  }),
) as Record<EmissionsMassUnitID, EmissionsMassUnit>;

export const emissionsMassUnitList: readonly EmissionsMassUnit[] = emissionsMassUnitIDs.map(
  (id) => emissionsMassUnits[id],
);

// Pickers offer these up front, in this order, and tuck the rest away:
const commonIDs: readonly EmissionsMassUnitID[] = ['tons', 'tonnes', 'pounds'];

export const emissionsMassUnitsCommon: readonly EmissionsMassUnit[] = commonIDs.map((id) => emissionsMassUnits[id]);

/** `emissionsMassUnitList` grouped by measurement system, for pickers. */
export const emissionsMassUnitSystems: readonly {
  system: EmissionsMassUnitSystem;
  name: string;
  units: readonly EmissionsMassUnit[];
}[] = (Object.keys(systemNames) as EmissionsMassUnitSystem[]).map((system) => ({
  system,
  name: systemNames[system],
  units: emissionsMassUnitList.filter((unit) => unit.system === system),
}));

/**
 * Converts a value to the given mass unit, or returns null when its units are not a mass. Non-finite values (i.e. an
 * incomplete calculation's NaN) pass through as-is.
 */
export function emissionsMassConvert(units: UnitsTracker, value: number, to: EmissionsMassUnitID): number | null {
  const converted = units.convertTo(emissionsMassUnits[to].units, value);
  return typeof converted === 'number' ? converted : null;
}

export function emissionsMassFromPounds(pounds: number, to: EmissionsMassUnitID): number {
  return pounds * emissionsMassUnits[to].perPound;
}
